import { memo, useCallback, useMemo } from 'react';
import * as R from 'ramda';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { ISelectOption } from 'UIPackage/MainModule/components/SelectInput';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import { IKnmSearchInitFilter } from '../interfaces/IKnmSearchInitFilter';
import styles from './SearchKnmPanelFilters.module.scss';
import { KnmSearchFilterDates } from './KnmSearchFilterDates';
import { KnmSearchFilterObject } from './KnmSearchFilterObject';
import { KnmSearchFilterArray } from './KnmSearchFilterArray';
import { KnmSearchFilterString } from './KnmSearchFilterString';
import { KnmSearchInitFilterKeys } from '../types/KnmSearchInitFilterKeys';
import { knmSearchFilterTitleMapper } from '../common/knmSearchFilterTitleMapper';
import { knmSearchGetData, knmSearchSetFilter } from '../actions/knmSearchActions';
import { getKnmSearchFilter } from '../selectors/knmSearchSelectors';

interface ISearchKnmPanelFiltersProps {
  filterValues: Partial<IKnmSearchInitFilter>;
}

const SearchKnmPanelFiltersWithoutStore = memo(({ filterValues }: ISearchKnmPanelFiltersProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const otherFilters = useMemo(() => R.omit(['fromDate', 'toDate'], filterValues), [filterValues]);
  const stringFilters = useMemo(() => R.filter(x => typeof x === 'string', otherFilters) as Record<string, string>, [
    otherFilters,
  ]);
  const arrayFilters = useMemo(
    () => R.filter(x => Array.isArray(x), otherFilters) as Record<string, Array<ISelectOption>>,
    [otherFilters],
  );
  const objectFilters = useMemo(
    () => R.filter(x => !Array.isArray(x) && typeof x !== 'string', otherFilters) as Record<string, ISelectOption>,
    [otherFilters],
  );

  const removeFilterValueFunc = useCallback(
    (title: KnmSearchInitFilterKeys) => {
      dispatch(knmSearchSetFilter(R.omit([title], filterValues)));
      dispatch(knmSearchGetData());
    },
    [dispatch, filterValues],
  );

  return (
    <div className={styles.Filters}>
      {Object.keys(stringFilters).map((filter: KnmSearchInitFilterKeys) => {
        function onRemove() {
          removeFilterValueFunc(filter);
        }

        return (
          <KnmSearchFilterString
            key={filter}
            filterName={knmSearchFilterTitleMapper(filter)}
            filterValue={stringFilters[filter]}
            removeFilterValue={onRemove}
          />
        );
      })}
      {Object.keys(arrayFilters).map((filter: KnmSearchInitFilterKeys) => (
        <KnmSearchFilterArray key={filter} filterName={filter} filterValue={arrayFilters[filter]} />
      ))}
      {Object.keys(objectFilters).map((filter: KnmSearchInitFilterKeys) => {
        function onRemove() {
          removeFilterValueFunc(filter);
        }

        return (
          <KnmSearchFilterObject
            key={filter}
            filterName={knmSearchFilterTitleMapper(filter)}
            filterValue={objectFilters[filter]}
            removeFilterValue={onRemove}
          />
        );
      })}
      {filterValues.fromDate && filterValues.toDate && (
        <KnmSearchFilterDates
          fromDate={filterValues.fromDate}
          toDate={filterValues.toDate}
          removeFilterValue={removeFilterValueFunc}
        />
      )}
    </div>
  );
});

const mapStateToProps = (state: IRootState) => ({
  filterValues: getKnmSearchFilter(state),
});

export const SearchKnmPanelFilters = connect(mapStateToProps)(SearchKnmPanelFiltersWithoutStore);
